<script setup>
import { ref, computed } from 'vue';
import { usePedidoStore } from '@/stores/pedido';
import useFormatters from '@/composables/useFormatters';
import MarcaLogo from '@/components/marcas/MarcaLogo.vue';

const props = defineProps({
  revenda: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['realizar-pedido', 'copy-id']);

const pedidoStore = usePedidoStore();
const { formatMoeda } = useFormatters();

// Computed properties
const temDesconto = computed(() => Number(props.revenda.valordesconto) > 0);
const descontoFormatado = computed(() => formatMoeda(props.revenda.valordesconto));
const valorFormatado = computed(() => formatMoeda(props.revenda.valor));
const valorCardFormatado = computed(() => formatMoeda(props.revenda.valorportaria));
const distanciaFormatada = computed(() => Number(props.revenda.distance).toFixed(2));
const avaliacaoFormatada = computed(() => Number(props.revenda.avaliacoes_nota_ponderada).toFixed(1));

// Verificar se é uma revenda nova (ativada nos últimos 7 dias)
const isNovaRevenda = computed(() => {
  if (!props.revenda.data_ativacao) return false;
  
  const dataAtivacao = new Date(props.revenda.data_ativacao);
  const hoje = new Date();
  const diffTime = Math.abs(hoje - dataAtivacao);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays <= 7;
});

// Informações sobre tipo de conta e crédito
const isContaPrePaga = computed(() => props.revenda.tipoconta === 'C');
const credito = computed(() => {
  if (!isContaPrePaga.value) return null;
  // Usar credito_int ou credito, o que estiver disponível
  const valor = props.revenda.credito_int || props.revenda.credito;
  return parseFloat(valor);
});
const temCreditoPositivo = computed(() => isContaPrePaga.value && credito.value > 0);
const creditoFormatado = computed(() => {
  if (!isContaPrePaga.value) return '';
  return credito;
});

// Verificar se é a revenda mais rápida
const isMaisRapida = computed(() => {
  return props.revenda.revendedor_mais_rapida === 1;
});

// Verificar se é Gás do Povo
const isGasDoPovo = computed(() => {
  return props.revenda.gas_do_povo === true;
});
// Computed for card background color
const cardBackgroundColor = computed(() => {
  // if (estaFechada.value) {
  //   return '#e5e5e5';
  // }
  if (isContaPrePaga.value) {
    if (!temCreditoPositivo.value) {
      return '#ff70fe';
    }
  }

  if (isMaisRapida.value) {
    return '#FFCE00';
  }

  if (isNovaRevenda.value) {
    return '#48fb66';
  }

  if (isContaPrePaga.value) {
    if (temCreditoPositivo.value) {
      return '#cee5fe';
    }
  }

  return '';
});

// Verificar se existe valor no cartão
const temValorCartao = computed(() => {
  return props.revenda.valorportaria && parseFloat(props.revenda.valorportaria) > 0;
});

const formatarParcelas = () => {
  if (!props.revenda.parcelamento || !props.revenda.qtdParcela) return '';
  
  const qtd = props.revenda.qtdParcela.qtdparcela;
  const valor = props.revenda.parcelamento.parcelamento[qtd - 1];
  
  return `${qtd}x ${formatMoeda(valor)}`;
};

// Modificar realizarPedido para verificar se está desabilitado
const realizarPedido = () => {
  if (isPedidoDesabilitado.value) {
    return; // Não faz nada se o pedido estiver desabilitado
  }
  emit('realizar-pedido', props.revenda);
};

// Função para emitir evento de cópia do ID da revenda - modificada com logs adicionais
const emitCopyId = (event) => {
  event.stopPropagation();
  const data = { text: 'número da revenda', value: props.revenda.idrevenda };
  emit('copy-id', data);
};

// Função para emitir evento de cópia da marca (fabricante)
const emitCopyFabricante = (event) => {
  event.stopPropagation();
  const data = { text: 'Marca do produto', value: props.revenda.fabricante };
  emit('copy-id', data);
};

// Função para emitir evento de cópia dos dados do produto
const emitCopyInfos = (event) => {
  event.stopPropagation();
  
  // Criar as linhas de informação dinamicamente com base no que está disponível
  const infoLinhas = [];
  
  infoLinhas.push(`À vista: ${valorFormatado.value}`);
  
  if (temValorCartao.value) {
    infoLinhas.push(`Cartão: ${valorCardFormatado.value}`);
  }
  
  const parcelasFormatadas = formatarParcelas();
  if (parcelasFormatadas) {
    infoLinhas.push(`Parcelado em até ${parcelasFormatadas}`);
  }
  
  if (temDesconto.value) {
    infoLinhas.push(`Desconto de ${descontoFormatado.value} se comprar mais de um botijão`);
  }
  
  const informacoes = `Valores:\n${infoLinhas.join('\n')}`;
  
  const data = { text: 'Informações de valores', value: informacoes };
  emit('copy-id', data);
};

// Função para formatar informações de tempo de entrega combinadas
const formatarTempoEntrega = () => {
  let texto = '';
  
  // Tempos informados pela revenda
  if (props.revenda.tempo_entrega1 && props.revenda.tempo_entrega2) {
    texto = `${props.revenda.tempo_entrega1} a ${props.revenda.tempo_entrega2} min`;
  } else {
    texto = 'Não informado';
  }
  
  // Adicionar tempo médio calculado se disponível
  if (props.revenda.tempo_medio_calculo) {
    const tempoMedioInteiro = Math.round(Number(props.revenda.tempo_medio_calculo));
    // Não incluir HTML aqui, apenas retorne os valores separados
    return {
      texto: texto,
      tempoMedio: tempoMedioInteiro
    };
  }
  
  return { texto: texto, tempoMedio: null };
}

// Função para abreviar os dias da semana
const abreviarDia = (dia) => {
  const abreviacoes = {
    'Segunda a Sexta': 'Segunda a Sexta',
    'Sábado': 'Sábado',
    'Domingo': 'Domingo'
  };
  
  return abreviacoes[dia] || dia;
};

// Função para verificar se a hora atual está dentro do horário de funcionamento
// para cada dia específico, não apenas para o dia atual
const verificarHorarioAberto = (descricao, horaInicio, horaFim) => {
  const agora = new Date();
  const diaSemana = agora.getDay(); // 0 = domingo, 1 = segunda, ..., 6 = sábado
  const horaAtual = agora.getHours();
  const minutoAtual = agora.getMinutes();
  const horaAtualTotal = horaAtual + (minutoAtual / 60);
  
  // Mapear dias da semana para os nomes usados na API
  const diasSemanaMap = {
    0: 'Domingo',
    1: 'Segunda a Sexta',
    2: 'Segunda a Sexta',
    3: 'Segunda a Sexta',
    4: 'Segunda a Sexta',
    5: 'Segunda a Sexta',
    6: 'Sábado'
  };
  
  // Verificar se é o dia atual
  const diaAtual = diasSemanaMap[diaSemana];
  if (descricao !== diaAtual) return false;
  
  // Converter horas de string "HH:MM" para número decimal
  const converterHoraParaDecimal = (horaString) => {
    const [horas, minutos] = horaString.split(':').map(Number);
    return horas + (minutos / 60);
  };
  
  const horaInicioDecimal = converterHoraParaDecimal(horaInicio);
  const horaFimDecimal = converterHoraParaDecimal(horaFim);
  
  // Verificar se a hora atual está dentro do horário de funcionamento
  return horaAtualTotal >= horaInicioDecimal && horaAtualTotal <= horaFimDecimal;
};

// Função para verificar se um dia específico é o dia atual
const ehDiaAtual = (descricao) => {
  const agora = new Date();
  const diaSemana = agora.getDay(); // 0 = domingo, 1 = segunda, ..., 6 = sábado
  
  // Mapear dias da semana para os nomes usados na API
  const diasSemanaMap = {
    0: 'Domingo',
    1: 'Segunda a Sexta',
    2: 'Segunda a Sexta',
    3: 'Segunda a Sexta',
    4: 'Segunda a Sexta',
    5: 'Segunda a Sexta',
    6: 'Sábado'
  };
  
  const diaAtual = diasSemanaMap[diaSemana];
  return descricao === diaAtual;
};

// Função para formatar o horário de funcionamento sem agrupar por horários iguais
const formatarHorariosFuncionamento = () => {
  if (!props.revenda.days || props.revenda.days.length === 0) {
    return 'Horário não informado';
  }
  
  return props.revenda.days.map(day => {
    const ehAtual = ehDiaAtual(day.descricao);
    const estaAberto = ehAtual ? verificarHorarioAberto(day.descricao, day.horainicio, day.horafim) : false;
    
    // Aplicar classe de cor apenas se for o dia atual
    const classeHorario = ehAtual 
      ? (estaAberto ? 'horario-aberto' : 'horario-fechado')
      : 'horario-normal';
    
    return `<div class="horario-dias ${classeHorario}">${abreviarDia(day.descricao)}: ${day.horainicio} - ${day.horafim}</div>`;
  }).join(' ');
};

const formatarNomeProduto = () => {

  if(props.revenda.produto == "Gás de Cozinha"){
    return 'P13';
  }else if(props.revenda.produto == "Botijão Comercial P"){
    return 'P45';
  }else if(props.revenda.produto == "Botijão para empilhadeira"){
    return 'P20';
  }else if(props.revenda.produto == "Doméstico" && props.revenda.peso == 5){
    return 'P5';
  }else if(props.revenda.produto == "Botijão Vazio"){
    return 'P13 Vazio';
  }else if(props.revenda.produto == "Doméstico" && props.revenda.peso == 8){
    return 'P8';
  }else if(props.revenda.produto == "Botijão COMPLETO (cheio)"){
    return 'P13 Completo';
  }else if(props.revenda.produto == "Botijão Comercial COMPLETO"){
    return 'P45 Completo';
  }else{
    return props.revenda.produto+ " " + props.revenda.peso+ "kg";
  }

};

// Verificar se a revenda está fechada (aberto = 0)
const estaFechada = computed(() => {
  return props.revenda.aberto === 0;
});

// Verificar se tem informação de reativação automática
const temReativacaoAutomatica = computed(() => {
  return estaFechada.value && 
         props.revenda.horario_da_reativacao_automatica && 
         props.revenda.horario_da_reativacao_automatica.reativacao_em;
});

// Verificar se a revenda está desabilitada para pedido
const isPedidoDesabilitado = computed(() => {
  if (temReativacaoAutomatica.value && props.revenda.horario_da_reativacao_automatica.observacao) {
    const observacao = props.revenda.horario_da_reativacao_automatica.observacao;
    return observacao === "ateAmanha" || observacao === "praSempre";
  }
  return false;
});

// Formatar a mensagem de agendamento
const mensagemAgendamento = computed(() => {
  if (estaFechada.value) {
    if (temReativacaoAutomatica.value) {
      const minutos = props.revenda.horario_da_reativacao_automatica.reativado_em_minutos;
      const observacao = props.revenda.horario_da_reativacao_automatica.observacao;
      
      if (isPedidoDesabilitado.value) {
        return {
          texto: observacao === "ateAmanha" 
            ? "Indisponível até amanhã" 
            : "Revenda permanentemente fechada",
          observacao: observacao,
          temReativacao: true,
          desabilitado: true
        };
      }
      
      return {
        texto: `Agendar pedido. Reativa em ${minutos} minutos`,
        observacao: observacao,
        temReativacao: true,
        desabilitado: false
      };
    } else {
      return {
        texto: "Agendar pedido",
        temReativacao: false,
        desabilitado: false
      };
    }
  }
  
  return null;
});

</script>

<template>
  <v-card 
    class="revenda-card"
    @click="realizarPedido"
    :ripple="!(isContaPrePaga && !temCreditoPositivo) && !isPedidoDesabilitado"
    hover
    :style="{ backgroundColor: cardBackgroundColor }"
    :class="{
      'revenda-fechada': estaFechada,
      'revenda-desabilitada': isPedidoDesabilitado
    }"
  >    
    <!-- Cabeçalho do Card -->
    <div class="card-header">
      <div class="header-content">
        <div class="header-title">
          <div class="d-flex align-center mb-1">
            <h3 class="text-h6 nome-revenda">
              {{ revenda.revendedor }}
              <span class="rating-indicator">
                <v-icon size="small" color="amber-darken-2">mdi-star</v-icon>
                {{ avaliacaoFormatada }}
              </span>
              
              <!-- Indicador de créditos para conta pré-paga -->
              <span v-if="isContaPrePaga" 
                    class="ml-2 credito-indicator" 
                    :class="{ 'credito-positivo': temCreditoPositivo, 'credito-negativo': !temCreditoPositivo }">
                 {{ creditoFormatado }} Créditos
              </span>
              
              <!-- Indicador de Gás do Povo -->
              <span v-if="isGasDoPovo" 
                    class="ml-2 gas-do-povo-indicator">
                <v-icon size="small" color="white">mdi-fire</v-icon>
                Gás do Povo
              </span>
            </h3>
          </div>
          <div class="d-flex align-center justify-space-between">

            <div class="revenda-id" @click.stop="emitCopyId">
              #{{ revenda.idrevenda }}
              <v-btn
                icon="mdi-content-copy"
                size="x-small"
                variant="text"
                density="comfortable"
                color="grey-lighten-1"
                @click.stop="emitCopyId"
                class="copy-btn ml-1"
                title="Copiar ID da revenda"
              ></v-btn>
            </div>

            <div class="revenda-id" :title="revenda.produto+' ' + revenda.peso + 'kg'">
              {{ formatarNomeProduto() }}
              <v-icon size="x-small" class="ml-1">mdi-propane-tank</v-icon>
            </div>

            <div class="revenda-id" @click.stop="emitCopyInfos">
              Informações
              <v-btn
                icon="mdi-content-copy"
                size="x-small"
                variant="text"
                density="comfortable"
                color="grey-lighten-1"
                @click.stop="emitCopyInfos"
                class="copy-btn ml-1"
                title="Copiar informacoes"
              ></v-btn>
            </div>
          </div>  
          
          <!-- Logo do fabricante -->
          <div @click.stop="emitCopyFabricante" class="mt-2">
            <MarcaLogo
              :logomarca="revenda.logomarca"
              :fabricante="revenda.fabricante"
              :width="180"
              class="mx-auto"
              title="Clique para copiar fabricante"
            />
          </div>
          
        </div>
      </div>
    </div>
    
    <v-divider class="m-0"></v-divider>
    
    <!-- Preço e Informações -->
    <div class="card-content">
      <div class="precos-container">

        <div class="preco-item">
          <div class="preco-label">
            <v-icon size="x-small" class="mr-1">mdi-cash</v-icon>
            À Vista
          </div>
          <div class="preco-valor">{{ valorFormatado }}</div>
        </div>
        
        <!-- Preço no Cartão (sempre visível se disponível) -->
        <template v-if="temValorCartao">
          <div class="divisor"></div>
          <div class="preco-item">
            <div class="preco-label">
              <v-icon size="x-small" class="mr-1">mdi-credit-card</v-icon>
              Cartão
            </div>
            <div class="preco-valor">{{ valorCardFormatado }}</div>
          </div>
        </template>
        
        <!-- Parcelamento (se disponível) -->
        <template v-if="revenda.statusparcela == 1">
          <div class="divisor"></div>
          <div class="preco-item">
            <div class="preco-label">
              <v-icon size="x-small" class="mr-1">mdi-calendar-clock</v-icon>
              Parcelado
            </div>
            <div class="parcelamento">{{ formatarParcelas() }}</div>
          </div>
        </template>
      </div>

      <div class="d-flex align-center justify-space-between">
        <div class="info-item">
          <v-icon class="mt-0" color="red" size="small">mdi-currency-usd</v-icon>
          <span style="color:red">Desconto 2º: {{ temDesconto ? descontoFormatado : "Não" }}</span>
        </div>
          <div class="info-item">
            <div class="pagamento-container">
              <div class="pagamento-metodos">
                <div
                  v-for="forma in revenda.fpg"
                  :key="forma.id"
                  class="me-1 mb-1"
                >
                  <v-icon start size="small" :title="forma.descricao">
                    {{ forma.id === 1 ? 'mdi-cash' : 
                      forma.id === 2 ? 'mdi-credit-card' : 
                      forma.id === 3 ? 'mdi-credit-card-outline' : 
                      forma.id === 4 ? 'mdi-cash-fast' : 'mdi-currency-usd' }}
                  </v-icon>
                </div>
              </div>
            </div>
        </div>
      </div>

      <div class="d-flex justify-space-between">
        <div class="info-item">
          <v-icon :color="isMaisRapida ? '#096767' : 'primary'" size="small">
            {{ isMaisRapida ? 'mdi-flash' : 'mdi-timer' }}
          </v-icon>
          <span :class="{ 'tempo-mais-rapido': isMaisRapida }">
            {{ formatarTempoEntrega().texto }}
            <span v-if="formatarTempoEntrega().tempoMedio" class="tempo-medio">
              (~{{ formatarTempoEntrega().tempoMedio }} min)
            </span>
          </span>
        </div>
      
        <div class="info-item">
          <v-icon color="primary" size="small">mdi-map-marker-distance</v-icon>
          {{ distanciaFormatada }} km
        </div>
      </div>

      <v-divider class="mb-1"></v-divider>
        
      <!-- Horários de funcionamento -->
      <div class="info-item">
        <v-icon color="primary" size="small">mdi-clock-outline</v-icon>
        <span class="horario-condensado" v-html="formatarHorariosFuncionamento()"></span>
      </div>

      <!-- Mensagem de Agendamento para revendas fechadas -->
      <template v-if="mensagemAgendamento">
        <v-divider class="mt-2 mb-2"></v-divider>
        <div class="agendamento-container" 
             :class="{
               'com-reativacao': mensagemAgendamento.temReativacao && !mensagemAgendamento.desabilitado,
               'desabilitado': mensagemAgendamento.desabilitado
             }">
          <div class="agendamento-icon">
            <v-icon 
              :color="mensagemAgendamento.desabilitado ? 'error' : 'warning'" 
              size="small"
            >
              {{ mensagemAgendamento.desabilitado ? 'mdi-block-helper' : 'mdi-calendar-clock' }}
            </v-icon>
          </div>
          <div class="agendamento-info">
            <div class="agendamento-texto">{{ mensagemAgendamento.texto }}</div>
            <div v-if="mensagemAgendamento.observacao && !isPedidoDesabilitado" class="agendamento-observacao">
              {{ mensagemAgendamento.observacao }}
            </div>
          </div>
        </div>
      </template>

    </div>
   
  </v-card>
</template>

<style scoped>
.revenda-card {
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  cursor: pointer;
  border: 1px solid #ddd
}

.revenda-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.1);
}

.revenda-card.fechada {
  opacity: 0.8;
  background-color: #f5f5f5;
}

.card-header {
  display: flex;
  align-items: center;
  padding: 10px;
}

.header-content {
  flex: 1;
}

.header-title {
  display: flex;
  flex-direction: column;
}

.nome-revenda {
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  font-size: 1.1rem; /* Increased from default */
}

.card-content {
  padding: 0 10px;
}

/* Adjusted styles for the prices container */
.precos-container {
  display: flex;
  margin: 0 -10px 12px;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 6px;
  padding: 4px 0;
}

.preco-item {
  padding: 0;
  flex: 1;
  text-align: center;
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.divisor {
  height: 30px;
  width: 1px;
  background-color: rgba(0, 0, 0, 0.1);
  margin: 0;
}

.preco-label {
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preco-valor {
  font-size: 1.15rem; /* Increased from 1rem */
  font-weight: 700;
  color: #1976d2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.parcelamento {
  font-size: 1.1rem; /* Increased from 0.95rem */
  font-weight: 600;
  color: #1976d2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.info-item {
  display: flex;
  align-items: flex-start;
  font-size: 1rem; /* Increased from 0.9rem */
  margin-bottom: 8px; /* Slightly increased from 6px for better spacing */
  line-height: 1.4; /* Slightly increased from 1.3 */
}

.info-item .v-icon {
  margin-right: 8px;
  margin-top: 1px;
  flex-shrink: 0;
}

.pagamento-container {
  margin-bottom: 0;
}

.pagamento-label {
  font-size: 1rem; /* Increased from 0.9rem */
  font-weight: 500;
  margin-bottom: 6px;
}

.pagamento-metodos {
  display: flex;
  flex-wrap: wrap;
}

.saldo-positivo {
  background-color: #4CAF50;
}

.saldo-negativo {
  background-color: #F44336;
}

.texto-positivo {
  color: #4CAF50;
  font-weight: 500;
}

.texto-negativo {
  color: #F44336;
  font-weight: 500;
}

.tipo-conta {
  font-weight: 500;
}

.horario-label {
  font-weight: 500;
  margin-right: 4px;
}

.horarios-lista {
  padding-left: 28px;
  margin-bottom: 8px;
}

.horario-item {
  font-size: 0.95rem; /* Increased from 0.85rem */
  margin-bottom: 2px;
  display: flex;
}

.horario-dia {
  font-weight: 500;
  width: 130px; /* Increased from 120px to accommodate larger font */
  flex-shrink: 0;
}

.horario-horas {
  color: #1976d2;
}

.nao-informado {
  padding-left: 28px;
  color: rgba(0, 0, 0, 0.6);
  font-style: italic;
  display: flex;
  align-items: center;
}

.tempo-mais-rapido {
  font-weight: 500;
  color: #096767;
}

.revenda-id {
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  border: 1px solid rgba(0, 0, 0, 0.1);
  padding: 0 4px; /* Slightly increased from 3px */
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.03);
}

/* Add specific styles for cards with different background colors */
.revenda-card[style*="background-color: #48fb66"] .nome-revenda,
.revenda-card[style*="background-color: #FFCE00"] .nome-revenda {
  color: rgba(0, 0, 0, 0.87);
}

/* Add a small indicator on top right for different card types */
.revenda-card::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 0;
  height: 0;
  z-index: 1;
}

.copy-btn {
  z-index: 30;
  position: relative;
  opacity: 0.75;
  transition: all 0.2s ease;
  background-color: rgba(255, 255, 255, 0.7);
}

.copy-btn:hover {
  opacity: 1;
  transform: scale(1.3);
  background-color: rgba(255, 255, 255, 0.8) !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.rating-indicator {
  display: inline-flex;
  align-items: center;
  font-size: 0.98rem;
  font-weight: 600;
  color: #FFA000;
  background-color: rgba(255, 193, 7, 0.2);
  border-radius: 10px;
  padding: 0px 5px 0px 2px;
  margin-left: 8px;
  vertical-align: middle;
  line-height: 1;
}

/* Ensure the rating is visible on colored backgrounds */
.revenda-card[style*="background-color"] .rating-indicator {
  background-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.horario-dias {
  font-weight: 500;
  color: rgba(0,0,0,0.8);
  font-size: 0.95rem;
  line-height: 1;
}

.horario-horas {
  color: #1976d2;
}

.horario-aberto {
  color: #096767 !important;
  font-weight: 600;
}

.horario-fechado {
  color: #e53935 !important;
  font-weight: 500;
}

.horario-normal {
  color: rgba(0, 0, 0, 0.87) !important;
  font-weight: 400;
}

:deep(.horario-aberto) {
  color: #096767 !important;
  font-weight: 600;
}

:deep(.horario-fechado) {
  color: #e53935 !important;
  font-weight: 600;
}

:deep(.horario-normal) {
  color: rgba(0, 0, 0, 0.87) !important;
  font-weight: 400;
}

.credito-indicator {
  display: inline-flex;
  align-items: center;
  text-wrap-mode: nowrap;
  font-size: 0.98rem; /* Increased from 0.85rem */
  font-weight: 600;
  border-radius: 10px;
  padding: 3px 4px 1px 5px;
  margin-left: 4px;
  vertical-align: middle;
  line-height: 1;
}

.credito-positivo {
  color: #4CAF50;
  background-color: rgba(76, 175, 80, 0.1);
}

.credito-negativo {
  color: #F44336;
  background-color: rgba(244, 67, 54, 0.1);
}

.revenda-card[style*="background-color"] .credito-indicator {
  background-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

/* Indicador de Gás do Povo */
.gas-do-povo-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-wrap-mode: nowrap;
  font-size: 0.98rem;
  font-weight: 600;
  border-radius: 10px;
  padding: 3px 8px 1px 8px;
  margin-left: 4px;
  vertical-align: middle;
  line-height: 1;
  background: linear-gradient(135deg, #DC1D27, #a01419);
  color: white;
  box-shadow: 0 2px 4px rgba(220, 29, 39, 0.3);
}

.revenda-card[style*="background-color"] .gas-do-povo-indicator {
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.agendamento-container {
  display: flex;
  align-items: flex-start;
  background-color: rgba(255, 193, 7, 0.1);
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 10px;
}

.agendamento-container.com-reativacao {
  background-color: rgba(255, 152, 0, 0.15);
  border-left: 3px solid #ff9800;
}

.agendamento-icon {
  margin-right: 8px;
  display: flex;
  align-items: center;
}

.agendamento-info {
  flex: 1;
}

.agendamento-texto {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  font-size: 1rem; /* Increased from 0.9rem */
  line-height: 1.3;
}

.agendamento-observacao {
  font-size: 0.9rem; /* Increased from 0.8rem */
  color: rgba(0, 0, 0, 0.6);
  margin-top: 2px;
}

.revenda-fechada {
  position: relative;
}

.revenda-card.revenda-fechada {
  opacity: 0.6;
  border: 1px solid #e0e0e0;
}

.revenda-card.revenda-desabilitada {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none !important;
  border: 1px solid #e0e0e0;
}

.revenda-card.revenda-desabilitada:hover {
  transform: none;
  box-shadow: none !important;
}

.agendamento-container.desabilitado {
  background-color: rgba(244, 67, 54, 0.1);
  border-left: 3px solid #f44336;
}

.revenda-desabilitada .agendamento-texto {
  color: #f44336;
}

/* Atualizar para adicionar o estilo para o novo contêiner de marca */
.marca-container {
  display: flex;
  align-items: center;
  padding: 3px 6px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

.tempo-medio {
  color: red;
  font-weight: 600;
}

</style>
